import { getClientId } from './clientId';
import { getAuthToken } from './auth';
import { API_ENV_TEST, getSelectedEnvironment } from '../utils/apiEnvironment';

const PROD_BASE = process.env.REACT_APP_SERVICE_ACCESS_ADMIN_PROD_URL
  || process.env.REACT_APP_SERVICE_ACCESS_ADMIN_API_URL
  || '';
const TEST_BASE = process.env.REACT_APP_SERVICE_ACCESS_ADMIN_TEST_URL
  || '';

export const SERVICE_ACCESS_STATUS_PATH = '/api-admin/v0/service-access/status';
export const SERVICE_ACCESS_ACTIONS_PATH = '/api-admin/v0/service-access/policy/actions';

function baseUrl() {
  return (getSelectedEnvironment() === API_ENV_TEST ? TEST_BASE : PROD_BASE).replace(/\/$/, '');
}

async function parseResponse(response) {
  const text = await response.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // Preserve the status-based error below for empty or non-JSON responses.
  }

  if (!response.ok) {
    const error = new Error(body?.msg || body?.message || body?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.code = body?.code;
    error.data = body;
    if (response.status === 409) {
      error.currentRevision = body?.data?.current_revision || body?.current_revision;
    }
    throw error;
  }
  return body?.data ?? body;
}

async function request(path, options = {}, accessToken = getAuthToken()) {
  if (!accessToken) {
    throw Object.assign(new Error('Not authenticated'), { status: 401 });
  }
  const response = await fetch(`${baseUrl()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'x-client-id': getClientId(),
      ...options.headers,
    },
  });
  return parseResponse(response);
}

export function getServiceAccessStatus(accessToken) {
  return request(SERVICE_ACCESS_STATUS_PATH, { method: 'GET' }, accessToken).then(normalizeStatus);
}

export function requestServiceAccessAction(input, accessToken) {
  return request(SERVICE_ACCESS_ACTIONS_PATH, {
    method: 'POST',
    body: JSON.stringify(input),
  }, accessToken);
}

export function normalizeStatus(status) {
  if (!status || typeof status !== 'object') return status;
  const meter = status.meter || {};

  return {
    ...status,
    policy: {
      ...status.policy,
      new_install_behavior: status.policy?.new_install_behavior || 'DENY',
    },
    meter: {
      admission_epoch: meter.admission_epoch || status.policy?.admission_epoch,
      admitted: meter.admitted,
      waiting: meter.waiting,
      admitted_semantics: meter.admitted_semantics,
      source: meter.source,
      collected_at: meter.collected_at,
    },
    history: Array.isArray(status.history) ? status.history.map((item) => ({
      ...item,
      created_at: item.created_at || item.published_at,
      reason: item.reason || item.change?.reason,
      actor: item.actor || item.change?.actor,
    })) : [],
  };
}
